import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Calendar, Newspaper, Users, ArrowRight, ChevronLeft, ChevronRight, Shield, Clock, Award, Heart, BookOpen, GraduationCap, HeartHandshake, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveFileUrl } from '../../utils/fileUrl';
import usePSDirectoryLaunch from '../../hooks/usePSDirectoryLaunch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Hero images - श्री हिंगलाज माता मंदिर images
const heroImages = [
  {
    url: 'https://customer-assets.emergentagent.com/job_khatri-directory/artifacts/ovj5mbua_Hinglaj%20Temple%20cover.jpeg',
    alt: ''
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_khatri-directory/artifacts/l9cmf9mn_Shaktipeeth%20Hinglaj.jpg',
    alt: ''
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_khatri-directory/artifacts/3fgpc6fp_Hansvahini%20Hinglaj.jpg',
    alt: ''
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [latestNews, setLatestNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const psDirectoryLaunched = usePSDirectoryLaunch();

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [newsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/news`),
        axios.get(`${API}/events`),
      ]);
      setLatestNews(newsRes.data.slice(0, 3));
      setUpcomingEvents(eventsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <div className="animate-fade-in">
      {/* Hero Section - 2 Column Layout */}
      <section className="relative min-h-[380px] overflow-hidden">
        {/* Background with gradient */}
        <div className="absolute inset-0 gradient-orange" />
        
        {/* Content Container */}
        <div className="relative w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row items-center lg:items-center min-h-[380px] gap-6 lg:gap-10 justify-center">
            
            {/* Left Column - Image */}
            <div className="order-2 lg:order-1 flex items-center justify-center flex-shrink-0">
              <div className="relative h-[320px] lg:h-[360px] w-[300px] lg:w-[320px] rounded-xl overflow-hidden shadow-2xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={heroImages[currentImageIndex].url}
                      alt={heroImages[currentImageIndex].alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </motion.div>
                </AnimatePresence>
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-800" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-5 h-5 text-gray-800" />
                </button>
                
                {/* Image Indicators */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className="p-2 flex items-center justify-center"
                      aria-label={`Go to image ${index + 1}`}
                    >
                      <span className={`block h-2 rounded-full transition-all ${
                        index === currentImageIndex
                          ? 'bg-white w-5'
                          : 'bg-white/50 hover:bg-white/70 w-2'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Center Column - Text Content */}
            <div className="order-1 lg:order-2 flex-1 flex items-center justify-center overflow-visible lg:pr-8">
              <div className="text-center px-4 lg:px-6 overflow-visible">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  style={{ overflow: 'visible' }}
                >
                {/* श्री हिंगुलायै नमः at top */}
                <div className="bg-white/15 backdrop-blur-sm rounded-lg px-5 py-2.5 inline-block mb-3">
                  <p className="text-base sm:text-lg text-white font-semibold">
                    श्री हिंगुलायै नमः
                  </p>
                </div>

                <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-2 leading-tight">ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)</h1>
                <p className="text-base sm:text-lg text-white/90 mb-3 font-medium">मारवाड़ी ब्रह्मक्षत्रिय समाज का केंद्रीय संगठन</p>
                <p className="text-base text-white/80 mb-4 leading-relaxed">
                  Akhil Bhartiya Marwadi Brahmkshatriya community united for social, cultural, and educational upliftment.
                </p>
                
                {/* Feature Tags */}
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <span className="flex items-center gap-2 text-white/90 text-sm sm:text-base">
                    <Shield size={16} />
                    Secure
                  </span>
                  <span className="text-white/40">|</span>
                  <span className="flex items-center gap-2 text-white/90 text-sm sm:text-base">
                    <Users size={16} />
                    Community
                  </span>
                  <span className="text-white/40">|</span>
                  <span className="flex items-center gap-2 text-white/90 text-sm sm:text-base">
                    <Award size={16} />
                    Heritage
                  </span>
                </div>
              </motion.div>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Important Links Section - Below Hero */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Important Links / महत्वपूर्ण लिंक
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/auth">
                <Button variant="outline" className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium">
                  <Users size={16} className="mr-2" />
                  Become Member
                </Button>
              </Link>
              <Link to="/scholarship/application">
                <Button variant="outline" className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium">
                  <GraduationCap size={16} className="mr-2" />
                  Apply Scholarship
                </Button>
              </Link>
              <Link to="/scholarship/donor-registration">
                <Button variant="outline" className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium">
                  <Heart size={16} className="mr-2" />
                  Contribute Scholarship
                </Button>
              </Link>
              <Link to="/conclave-registration">
                <Button variant="outline" className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium">
                  <Calendar size={16} className="mr-2" />
                  Register Event
                </Button>
              </Link>
              <Link to="/families-register">
                <Button variant="outline" className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium">
                  <BookOpen size={16} className="mr-2" />
                  Feed Family Data
                </Button>
              </Link>
              {/* Parichay Sangam Directory — auto-enables 13 Jun 2026 09:00 IST */}
              {psDirectoryLaunched ? (
                <Link to="/parichay-sangam/directory">
                  <Button
                    variant="outline"
                    className="h-10 border-gray-200 hover:border-primary hover:bg-primary/5 hover:text-primary text-sm font-medium"
                    data-testid="ps-directory-quicklink"
                  >
                    <HeartHandshake size={16} className="mr-2" />
                    PS Directory
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="outline"
                  disabled
                  title="Available from 13 June 2026, 9:00 AM IST"
                  className="h-10 border-gray-200 text-sm font-medium opacity-60 cursor-not-allowed relative"
                  data-testid="ps-directory-quicklink-disabled"
                >
                  <HeartHandshake size={16} className="mr-2" />
                  PS Directory
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                    13 Jun
                  </span>
                </Button>
              )}
              <Link to="/auth">
                <Button className="h-10 bg-primary hover:bg-primary-600 text-white font-semibold text-sm">
                  Login
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Convention 2026 Announcement Banner */}
      <section className="py-10 bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3] relative overflow-hidden" data-testid="vc-hero-banner">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#7C2027] rounded-full -translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#1E3A5F] rounded-full translate-x-1/4 translate-y-1/4" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border-2 border-[#B08A2E]/30 bg-white/70 backdrop-blur-sm shadow-xl overflow-hidden"
          >
            <div className="grid md:grid-cols-[220px_1fr] gap-6 p-6 md:p-8 items-center">
              {/* Logo */}
              <div className="flex justify-center">
                <img
                  src="/vc-logo-seal.png"
                  alt="Vision Sangam 2026 Seal"
                  className="w-40 h-40 md:w-48 md:h-48 object-contain drop-shadow-lg"
                  data-testid="vc-banner-logo"
                />
              </div>

              {/* Content */}
              <div className="text-center md:text-left">
                <span className="inline-flex items-center gap-1.5 bg-[#7C2027] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  <Sparkles className="w-3.5 h-3.5" /> Upcoming Event
                </span>
                <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-[#7C2027] leading-tight">
                  समाज चिंतन एवं विज़न संगम – 2026
                </h2>
                <p className="mt-1 text-sm sm:text-base italic text-[#B08A2E] font-semibold" data-testid="vc-banner-tagline">
                  संगम विचारों का, संकल्प भविष्य का
                </p>
                <p className="mt-1 text-sm sm:text-base text-[#1E3A5F] font-semibold">
                  Vision Sangam 2026 · September 2026 · Jodhpur
                </p>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed max-w-2xl">
                  समाज के वर्तमान एवं भविष्य से जुड़े महत्वपूर्ण विषयों पर दूरदर्शी कार्ययोजना तैयार करने हेतु आयोजित एक ऐतिहासिक चिंतन।
                  <span className="hidden sm:inline"> ₹500 का सहयोग-योगदान देकर पंजीकरण करें।</span>
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <Link to="/vision-convention-2026" data-testid="vc-banner-register-btn">
                    <Button
                      size="lg"
                      className="bg-[#7C2027] hover:bg-[#5A171C] text-white font-bold px-6 py-6 rounded-xl shadow-lg"
                    >
                      Register Now / अभी पंजीकरण करें
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-3 py-1.5" data-testid="vc-banner-last-date">
                    Last date: <span className="line-through opacity-70">31 August 2026</span>&nbsp;<span className="font-bold">Extended to 30 September 2026</span>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Parichay Sangam 2026 Hero Banner */}
      <section className="py-10 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 relative overflow-hidden" data-testid="ps-hero-banner">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/4 translate-y-1/4" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              🌸 कार्यक्रम सफलतापूर्वक संपन्न / Event Concluded
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
              परिचय संगम 2026
            </h2>
            <p className="text-white/90 text-base sm:text-lg mb-2">Parichay Sangam 2026 — Matrimonial Meet</p>
            <p className="text-white/80 text-sm max-w-xl mx-auto mb-6">
              पंजीकृत प्रत्याशियों की PDF ई-बुक एवं ऑनलाइन मैट्रीमोनियल डायरेक्टरी अब उपलब्ध है।<br />
              The complete e-Book and online matrimonial directory are now live.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {psDirectoryLaunched ? (
                <Link to="/parichay-sangam/directory" data-testid="ps-hero-directory-btn">
                  <Button size="lg" className="bg-white text-orange-700 hover:bg-orange-50 font-bold px-6 py-6 rounded-xl shadow-lg text-base">
                    <Users className="w-5 h-5 mr-2" />
                    ऑनलाइन डायरेक्टरी / View Directory
                  </Button>
                </Link>
              ) : (
                <Button
                  size="lg"
                  disabled
                  title="Available from 13 June 2026, 9:00 AM IST"
                  className="bg-white/70 text-orange-700 font-bold px-6 py-6 rounded-xl shadow-lg text-base cursor-not-allowed"
                  data-testid="ps-hero-directory-btn"
                >
                  <Users className="w-5 h-5 mr-2" />
                  ऑनलाइन डायरेक्टरी (13 जून से)
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Welcome to Khatrisangam Section */}
      <section className="py-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              खत्रीसंगम में आपका स्वागत है
            </h2>
            <p className="text-gray-500 text-lg">Welcome to Khatrisangam</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg border border-orange-100 p-8 md:p-10"
          >
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
              <p className="text-lg">
                <span className="text-primary font-semibold">ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)</span> द्वारा सृजित व ब्रह्मक्षत्रिय समाज को समर्पित इस वेब साइट में आपका स्वागत है।
              </p>
              
              <p>
                इस वेबसाइट का एक हिस्सा <strong className="text-primary">समाज के बारे में (About Samaj)</strong> नामक विभाग से है। इसमें दी गयी सामग्री हमारे समाज के प्रकाशनों, वेब साइट्स, संस्थाओं के प्रतिवेदन, समाज बंधुओं से बातचीत, इंटरनेट व सार्वजनिक मंचों पर उपलब्ध जानकारियों व छाया चित्रों और कार्यकर्ताओं की जानकारी पर आधारित है। समाज व सामाजिक गतिविधियों पर सकारात्मक दृष्टिकोण केंद्रित किया गया है। आशा है इस वेबसाइट पर संचित ये जानकारियां सभी के लिये विशेषतः नयी पीढी व आने वाली पीढियों के लिये उपयोगी व ज्ञानवर्धक सिद्ध होगी।
              </p>
              
              <p>
                <strong className="text-primary">फैमीलीज डाटा</strong> में सभी परिवारों व परिवारजनों का विस्तृत विवरण फीड करने हेतु आप आमंत्रित हैं। इससे परिवारों का बहुउद्देश्यीय डिजीटल रजिस्टर बनेगा, जो महत्वपूर्ण व उपयोगी सिद्ध होगा। विवाह योग्य युवक युवतियों की जानकारी भी इस रजिस्टर से मिलेगी तथा इस उद्देश्य के लिये समम समय पर विशेष प्रबंध जैसे परिचय संगम भी आयोजित किये जायेंगे।
              </p>
              
              <p>
                <strong className="text-primary">प्रोफेशनल्स डायरेक्टरी</strong> के अन्तर्गत समाज के विभिन्न क्षेत्रों के विशेषज्ञ जैसे प्रशासनिक अधिकारी, राजनीतिज्ञ, उद्यमी, प्रोफेशनल्स, समाजसेवी आदि अपना विवरण फीड करने हेतु आमंत्रित हैं। इससे बनने वाली डायरेक्टरी से प्रोफेशनल्स अपना नेटवर्क स्थापित कर सकेंगे व समाज बंधु भी उनके बारे में जान सकेंगे। इनके वेबीनार, सेमीनार, कॉन्क्लेव, कॉन्फ्रेंस आदि भी आयोजित करने का प्रयास किया जायेगा।
              </p>
              
              <p>
                घटनाओं, गतिविधियों व समाचारों का भी समावेश वेब साइट में किया जायेगा।
              </p>
              
              <p>
                इस प्रकार समाज की संस्कृति, इतिहास, वर्तमान स्थिति व समाज की धरोहरों का प्रकाशन कर यह वेबसाइट भी <span className="text-primary font-semibold">समाज की धरोहर</span> बनेगी। समाज संगठन व सामाजिक गतिविधियों को संचालित करने का मंच भी यह वेबसाइट बनेगी। इस डिजिटल क्रांति में समाज के प्रत्येक बंधु की सहभागिता अपेक्षित है।
              </p>
              
              <p className="text-center text-xl font-bold text-primary border-t border-orange-100 pt-6 mt-6">
                जय श्री हिंगलाज।
              </p>
            </div>
            
            {/* Call to Action */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/hinglaj-kuldevi">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                  समाज के बारे में जानें
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/family-registration">
                <Button className="bg-primary hover:bg-primary-600 text-white">
                  परिवार पंजीकरण करें
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/professional/register">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  प्रोफेशनल पंजीकरण करें
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section - Same as About Page Mission */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 section-heading">
              Our Mission
            </h2>
            <p className="text-gray-500 text-lg">हमारा उद्देश्य</p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Unity / एकता',
                description: 'समस्त मारवाड़ी ब्रह्मक्षत्रिय समाज में एकता स्थापित करना और विविध सदस्यों को एक साथ लाना।',
              },
              {
                icon: <BookOpen className="w-6 h-6" />,
                title: 'Social & Educational Upliftment',
                description: 'समाजिक, सांस्कृतिक, आर्थिक व शैक्षणिक क्षेत्र में सर्वांगीण उन्नति करना।',
              },
              {
                icon: <Heart className="w-6 h-6" />,
                title: 'Community Welfare',
                description: 'समाज के आबालवृद्ध, अनाथ, उपेक्षित, असहाय व कमजोर अंगों की सहायता करना।',
              },
              {
                icon: <Award className="w-6 h-6" />,
                title: 'Heritage & Culture',
                description: 'सामाजिक व सांस्कृतिक परम्पराओं को अक्षुण्ण रखते हुये बदलते समय के अनुसार परिष्कृत करना।',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-card-hover transition-all duration-300 card-hover"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - ICICI Style Orange Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 section-heading"
          >
            Services & Programs
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Membership Registration',
                badge: 'POPULAR',
                description: 'Join our growing community',
                features: ['Digital membership card', 'Member directory access', 'Event participation'],
                link: '/auth',
                linkText: 'Register Now'
              },
              {
                title: 'Conclave 2026',
                badge: 'UPCOMING',
                description: 'Annual community gathering',
                features: ['Networking sessions', 'Cultural programs', 'Business opportunities'],
                link: '/conclave-registration',
                linkText: 'Register',
                disabled: true
              },
              {
                title: 'Scholarships',
                badge: 'EDUCATION',
                description: 'Supporting deserving students',
                features: ['Merit-based scholarships', 'Need-based assistance', 'Higher education support'],
                link: '/scholarships',
                linkText: 'Apply Now'
              },
              {
                title: 'Donor Program',
                badge: 'CONTRIBUTE',
                description: 'Support community initiatives',
                features: ['Transparent fund usage', 'Tax benefits', 'Recognition programs'],
                link: '/donor-registration',
                linkText: 'Donate'
              },
              {
                title: 'News & Updates',
                badge: 'STAY INFORMED',
                description: 'Latest community news',
                features: ['Community announcements', 'Event coverage', 'Achievement highlights'],
                link: '/programs-and-news',
                linkText: 'Read More'
              },
              {
                title: 'Events & Programs',
                badge: 'CONNECT',
                description: 'Community gatherings',
                features: ['Cultural events', 'Religious programs', 'Social gatherings'],
                link: '/events',
                linkText: 'View Events'
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 card-hover"
              >
                {/* Orange Header */}
                <div className="gradient-orange p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <h3 className="text-xl font-bold text-white relative z-10">{service.title}</h3>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <span className="inline-block bg-primary text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                    {service.badge}
                  </span>
                  <p className="text-gray-700 font-medium mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link} className={service.disabled ? 'pointer-events-none' : ''} aria-disabled={service.disabled || undefined} tabIndex={service.disabled ? -1 : undefined}>
                    <Button
                      variant="outline"
                      disabled={service.disabled}
                      className={`w-full border-gray-200 font-semibold ${
                        service.disabled
                          ? 'text-gray-400 cursor-not-allowed opacity-60 hover:border-gray-200 hover:text-gray-400'
                          : 'text-gray-700 hover:border-primary hover:text-primary'
                      }`}
                    >
                      {service.disabled ? 'Coming Soon' : service.linkText}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclave Banner temporarily hidden */}

      {/* Latest News Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 section-heading">Latest News</h2>
            <Link to="/programs-and-news">
              <Button variant="outline" className="border-gray-200 text-gray-700 hover:border-primary hover:text-primary">
                View All <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestNews.length > 0 ? (
              latestNews.map((news, index) => (
                <motion.div
                  key={news.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 card-hover cursor-pointer"
                  onClick={() => navigate('/programs-and-news')}
                  data-testid={`homepage-news-card-${index}`}
                >
                  {news.image && (
                    <div className="h-48 bg-gray-100 overflow-hidden">
                      <img
                        src={resolveFileUrl(news.image)}
                        alt={news.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="text-sm text-primary font-semibold">
                      {new Date(news.date).toLocaleDateString()}
                    </span>
                    <h3 className="font-bold text-lg text-gray-900 mt-2 mb-2 line-clamp-2">
                      {news.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-3">{news.content}</p>
                    <span className="mt-3 text-primary font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
                      Read More <ArrowRight size={16} />
                    </span>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-3 text-center py-12 bg-white rounded-xl">
                <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No news available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 section-heading">Upcoming Events</h2>
            <Link to="/events">
              <Button variant="outline" className="border-gray-200 text-gray-700 hover:border-primary hover:text-primary">
                View All <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          <div className="space-y-4">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 border border-gray-100 shadow-card hover:shadow-card-hover transition-all duration-300 flex flex-col md:flex-row gap-6"
                >
                  {event.image && (
                    <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={resolveFileUrl(event.image)}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                      <Calendar size={16} />
                      <span className="text-sm">
                        {new Date(event.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-2">{event.description}</p>
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold">Location:</span> {event.location}
                    </p>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No upcoming events</p>
              </div>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
